import React, { useState, useEffect } from 'react';
import ProgressBar from './components/ProgressBar';
import Step1Alignment from './components/Step1Alignment';
import Step2Constitution from './components/Step2Constitution';
import Step3Tactical from './components/Step3Tactical';
import SummaryReport from './components/SummaryReport';
import Exercises from './components/Exercises';
import UserProfileForm from './components/UserProfileForm';
import './App.css';

function App() {
  // Main state for the entire application
  const [currentView, setCurrentView] = useState('user-profile'); // Start with user profile
  const [currentStep, setCurrentStep] = useState(1);
  const [userProfile, setUserProfile] = useState(null);
  const [formData, setFormData] = useState({
    // Step 1 - Alignment
    nationalPriority: null,
    nationalIncentiveClaimed: null,
    nationalActionSteps: '',
    statePriority: null,
    stateIncentiveClaimed: null,
    stateActionSteps: '',
    
    // Step 2 - Constitution Optimization
    constitutionOptimized: null,
    constitutionActionSteps: '',
    bulletproofTest: null,
    bulletproofActionSteps: '',
    
    // Step 3 - Tactical Strategies
    tacticalStrategies: null,
    tacticalActionSteps: ''
  });

  // Load data from localStorage on component mount
  useEffect(() => {
    const savedData = localStorage.getItem('taxStructuringData');
    const savedProfile = localStorage.getItem('userProfileFormData');
    
    if (savedData) {
      try {
        const parsedData = JSON.parse(savedData);
        // Validate the parsed data structure
        if (parsedData && typeof parsedData === 'object') {
          setFormData(parsedData);
        } else {
          console.warn('Invalid saved data structure, using defaults');
          localStorage.removeItem('taxStructuringData');
        }
      } catch (error) {
        console.error('Error loading saved form data:', error);
        localStorage.removeItem('taxStructuringData');
      }
    }
    
    if (savedProfile) {
      try {
        const profileData = JSON.parse(savedProfile);
        // Check if profile is complete
        if (isProfileComplete(profileData)) {
          setUserProfile(profileData);
          setCurrentView('tax-structuring');
        }
      } catch (error) {
        console.error('Error loading saved profile data:', error);
        localStorage.removeItem('userProfileFormData');
      }
    }
  }, []);

  // Save data to localStorage whenever formData changes
  useEffect(() => {
    localStorage.setItem('taxStructuringData', JSON.stringify(formData));
  }, [formData]);

  // Check if user profile is complete
  const isProfileComplete = (profileData) => {
    if (!profileData) return false;
    
    const requiredFields = ['firstName', 'lastName', 'email', 'roles', 'country', 'ageGroup'];
    
    // Check all required fields
    for (let field of requiredFields) {
      if (!profileData[field] || (Array.isArray(profileData[field]) && profileData[field].length === 0)) {
        return false;
      }
    }
    
    // Check CA/CPA Status if it should be shown
    const shouldShowCaStatus = profileData.roles.includes('Chartered Accountant') || 
                              profileData.roles.includes('CPA or Tax Advisor');
    if (shouldShowCaStatus && !profileData.caStatus) {
      return false;
    }
    
    return true;
  };

  // Handle profile completion
  const handleProfileComplete = (profileData) => {
    setUserProfile(profileData);
    // Don't automatically proceed to tax structuring - let user choose when to start
    setCurrentView('profile-complete');
  };

  // Handle starting the tax structuring assessment
  const handleStartAssessment = () => {
    setCurrentView('tax-structuring');
    setCurrentStep(1);
  };

  // Update form data
  const updateFormData = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Navigation functions
  const nextStep = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const goToStep = (step) => {
    setCurrentStep(step);
  };

  // Check if current step is complete
  const isStepComplete = (step) => {
    switch (step) {
      case 1:
        return formData.nationalPriority !== null && 
               formData.nationalIncentiveClaimed !== null && 
               formData.nationalActionSteps.trim() !== '' &&
               formData.statePriority !== null && 
               formData.stateIncentiveClaimed !== null && 
               formData.stateActionSteps.trim() !== '';
      case 2:
        return formData.constitutionOptimized !== null && 
               formData.constitutionActionSteps.trim() !== '' &&
               formData.bulletproofTest !== null && 
               formData.bulletproofActionSteps.trim() !== '';
      case 3:
        return formData.tacticalStrategies !== null && 
               formData.tacticalActionSteps.trim() !== '';
      case 4:
        // Check if exercises are completed (at least 50% based on the Exercises component logic)
        const savedExercises = localStorage.getItem('taxAssessmentExercises');
        if (savedExercises) {
          try {
            const exercises = JSON.parse(savedExercises);
            const completedCount = Object.keys(exercises).filter(key => {
              const exercise = exercises[key];
              switch (key) {
                case 'exercise1':
                  return exercise.country && exercise.priority1 && exercise.paranoia1 && exercise.prohibition1;
                case 'exercise2':
                  return exercise.countries.some(c => c.country && c.category);
                case 'exercise3':
                  return exercise.jobs || exercise.salaryExpenses || exercise.technologyCosts || exercise.exportRevenues;
                case 'exercise4':
                  return exercise.builders.some(b => b) || exercise.enablers.some(e => e) || 
                         exercise.thrivers.some(t => t) || exercise.supportees.some(s => s);
                case 'exercise5':
                  return exercise.industry && exercise.country1.name;
                case 'exercise6':
                  return exercise.country && exercise.currency1;
                case 'exercise7':
                  return exercise.age10 || exercise.age20 || exercise.age30 || exercise.age40;
                case 'exercise8':
                  return true; // Quiz is always complete
                default:
                  return false;
              }
            }).length;
            return completedCount >= 4; // At least 4 out of 8 exercises completed
          } catch (error) {
            return false;
          }
        }
        return false;
      case 5:
        // Summary Report is always complete once you reach it
        return true;
      case 6:
        // Final completion is always complete once you reach it
        return true;
      default:
        return false;
    }
  };

  const isAllStepsComplete = () => {
    return isStepComplete(1) && isStepComplete(2) && isStepComplete(3) && isStepComplete(4) && isStepComplete(5) && isStepComplete(6);
  };

  // Render current step
  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <Step1Alignment
            formData={formData}
            updateFormData={updateFormData}
            onNext={nextStep}
          />
        );
      case 2:
        return (
          <Step2Constitution
            formData={formData}
            updateFormData={updateFormData}
            onNext={nextStep}
            onPrev={prevStep}
          />
        );
      case 3:
        return (
          <Step3Tactical
            formData={formData}
            updateFormData={updateFormData}
            onPrev={prevStep}
            onComplete={async () => {
              // Send completion email if user has provided an email
              const userEmail = localStorage.getItem('userEmail');
              if (userEmail && userEmail.trim()) {
                try {
                  console.log('Sending assessment completion email...');
                  const response = await fetch('http://localhost:5001/api/send-completion-email', {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                      email: userEmail.trim(),
                      formData: formData,
                      completionType: 'Assessment Steps'
                    }),
                  });
                  
                  if (response.ok) {
                    const result = await response.json();
                    if (result.success) {
                      console.log('✅ Assessment completion email sent successfully');
                    }
                  }
                } catch (error) {
                  console.error('Failed to send completion email:', error);
                }
              }
              
              // Go directly to Exercises (classroom interface)
              setCurrentStep(5);
            }}
          />
        );
      case 4:
        return (
          <Exercises
            onBack={() => setCurrentStep(3)} // Back button goes to Step 3
            onExercisesComplete={(exercisesData) => {
              // After completing exercises, go to Summary Report
              setCurrentStep(5);
            }}
          />
        );
      case 5:
        return (
          <SummaryReport
            formData={formData}
            onBack={() => setCurrentStep(4)} // Back button goes to Exercises
            onNext={() => setCurrentStep(6)} // Next button goes to final completion
            onRestart={() => {
              setCurrentStep(1);
              setFormData({
                nationalPriority: null,
                nationalIncentiveClaimed: null,
                nationalActionSteps: '',
                statePriority: null,
                stateIncentiveClaimed: null,
                stateActionSteps: '',
                constitutionOptimized: null,
                constitutionActionSteps: '',
                bulletproofTest: null,
                bulletproofActionSteps: '',
                tacticalStrategies: null,
                tacticalActionSteps: ''
              });
            }}
          />
        );
      case 6:
        return (
          <div className="final-completion">
            <h2>🎉 Congratulations!</h2>
            <p>You have completed the entire Tax Structuring Assessment and Exercises!</p>
            <div className="completion-actions">
              <button 
                className="btn btn-primary"
                onClick={() => setCurrentStep(5)} // Go to Summary Report
              >
                View Summary Report
              </button>
              <button 
                className="btn btn-secondary"
                onClick={() => setCurrentStep(4)} // Go back to Exercises
              >
                Review Exercises
              </button>
              <button 
                className="btn btn-secondary"
                onClick={() => {
                  setCurrentStep(1);
                  setFormData({
                    nationalPriority: null,
                    nationalIncentiveClaimed: null,
                    nationalActionSteps: '',
                    statePriority: null,
                    stateIncentiveClaimed: null,
                    stateActionSteps: '',
                    constitutionOptimized: null,
                    constitutionActionSteps: '',
                    bulletproofTest: null,
                    bulletproofActionSteps: '',
                    tacticalStrategies: null,
                    tacticalActionSteps: ''
                  });
                }}
              >
                Start Over
              </button>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="App">
      <div className="container">
        <header className="app-header">
          <h1 className="app-title">Tax Structuring Platform</h1>
          <p className="app-subtitle">Interactive 3-Step Domestic Tax Structuring Decision Tool</p>
          
          {/* Show user info if profile is complete */}
          {userProfile && (
            <div className="user-info">
              <p>Welcome, {userProfile.firstName} {userProfile.lastName}</p>
              <button 
                className="edit-profile-btn"
                onClick={() => {
                  setCurrentView('user-profile');
                  setUserProfile(null);
                }}
              >
                Edit Profile
              </button>
            </div>
          )}
        </header>

        {currentView === 'user-profile' ? (
          <UserProfileForm onProfileComplete={handleProfileComplete} />
        ) : currentView === 'profile-complete' ? (
          <div className="profile-complete-container">
            <div className="profile-complete-content">
              <h2>Profile Completed Successfully! 🎉</h2>
              <p>Welcome, {userProfile.firstName} {userProfile.lastName}!</p>
              <p>Your profile has been saved. You can now start the Tax Structuring Assessment.</p>
              
              <div className="profile-summary">
                <h3>Profile Summary:</h3>
                <p><strong>Email:</strong> {userProfile.email}</p>
                <p><strong>Professional Roles:</strong> {userProfile.roles.join(', ')}</p>
                <p><strong>Country:</strong> {userProfile.country}</p>
                <p><strong>Age Group:</strong> {userProfile.ageGroup}</p>
                {userProfile.caStatus && (
                  <p><strong>CA Status:</strong> {userProfile.caStatus}</p>
                )}
              </div>
              
              <div className="profile-actions">
                <button 
                  className="btn btn-primary"
                  onClick={handleStartAssessment}
                >
                  Start Tax Structuring Assessment
                </button>
                <button 
                  className="btn btn-secondary"
                  onClick={() => setCurrentView('user-profile')}
                >
                  Edit Profile
                </button>
              </div>
            </div>
          </div>
        ) : (
          <>
            <ProgressBar 
              currentStep={currentStep} 
              isStepComplete={isStepComplete}
              goToStep={goToStep}
            />

            {renderCurrentStep()}
          </>
        )}
      </div>
    </div>
  );
}

export default App;
